'use client';

/**
 * ExpandingCapsule — the "+ New" spring capsule (T8). At rest it's a fixed-
 * width pill showing only the plus icon; on hover or keyboard focus it
 * springs wider and reveals the label.
 *
 * The label's reveal width is measured via ref (`scrollWidth`) rather than a
 * guessed constant, so a custom `label` longer than "New" still gets an
 * accurate auto-fit instead of being clipped or leaving dead space — capped
 * at MAX_LABEL_WIDTH only as a guard against pathologically long strings.
 *
 * IMPORTANT: this component animates its own `width`. It must sit in a flex
 * row alongside a `flex-1` sibling (e.g. a search input) so the row's total
 * width stays constant as the capsule grows — otherwise the row will jump or
 * overflow its container on hover.
 */
import { useLayoutEffect, useRef, useState, type FocusEvent } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  V3_COLORS,
  PRESSABLE_SCALE,
  PRESSABLE_DURATION_MS,
} from '@/lib/design-tokens';

export const CAPSULE_SIZE = {
  MD: 'md',
  SM: 'sm',
} as const;
export type CapsuleSize = (typeof CAPSULE_SIZE)[keyof typeof CAPSULE_SIZE];

const DIMENSIONS: Record<
  CapsuleSize,
  { height: number; restWidth: number; radius: number }
> = {
  [CAPSULE_SIZE.MD]: { height: 40, restWidth: 54, radius: 20 },
  [CAPSULE_SIZE.SM]: { height: 36, restWidth: 52, radius: 18 },
};

const ICON_SIZE = 19;
const GAP_PX = 6;
const PADDING_LEFT_EXPANDED = 13;
const PADDING_RIGHT_EXPANDED = 16;
/** Safety cap on the measured label width, guarding against an unexpectedly
 *  long custom `label` blowing the capsule out to an unreasonable size. */
const MAX_LABEL_WIDTH = 120;

const REST_SHADOW = '0 10px 22px -12px rgba(227,96,1,.75)';
const HOVER_SHADOW = '0 15px 28px -10px rgba(227,96,1,.85)';

// transform's duration is PRESSABLE_DURATION_MS — this capsule is the design
// reference every other pressable control is standardized against (see
// design-tokens.ts), so its own press timing is sourced from the same shared
// constant rather than a second hardcoded literal.
const CONTAINER_TRANSITION = `width .34s cubic-bezier(.34,1.42,.5,1), padding .34s cubic-bezier(.34,1.42,.5,1), background-color .2s ease, box-shadow .34s ease, transform ${PRESSABLE_DURATION_MS}ms ease`;
const LABEL_TRANSITION =
  'max-width .34s cubic-bezier(.34,1.42,.5,1), opacity .24s ease, margin-left .34s ease';

export interface ExpandingCapsuleProps {
  size?: CapsuleSize;
  label?: string;
  onClick?: () => void;
  /** Overrides the accessible name (default falls back to the visible label
   *  text, which stays in the a11y tree even while visually clipped). */
  ariaLabel?: string;
  className?: string;
}

export function ExpandingCapsule({
  size = CAPSULE_SIZE.MD,
  label = 'New',
  onClick,
  ariaLabel,
  className,
}: ExpandingCapsuleProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useLayoutEffect(() => {
    if (labelRef.current) {
      setLabelWidth(Math.min(labelRef.current.scrollWidth, MAX_LABEL_WIDTH));
    }
  }, [label]);

  const expanded = hovered || focused;
  const { height, restWidth, radius } = DIMENSIONS[size];
  const expandedWidth =
    PADDING_LEFT_EXPANDED +
    ICON_SIZE +
    GAP_PX +
    labelWidth +
    PADDING_RIGHT_EXPANDED;

  const handleFocus = (e: FocusEvent<HTMLButtonElement>) => {
    // Only expand for keyboard focus, not the mouse-focus that follows a
    // click (hover already covers the mouse case).
    if (e.target.matches(':focus-visible')) setFocused(true);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onFocus={handleFocus}
      onBlur={() => setFocused(false)}
      aria-label={ariaLabel}
      className={cn(
        "flex shrink-0 items-center overflow-hidden font-['Work_Sans'] text-[13px] font-semibold text-white",
        className
      )}
      style={{
        height,
        width: expanded ? expandedWidth : restWidth,
        borderRadius: radius,
        padding: expanded ? '0 16px 0 13px' : '0 0 0 18px',
        backgroundColor: pressed
          ? V3_COLORS.orangeActive
          : expanded
            ? V3_COLORS.orangeHover
            : V3_COLORS.orange,
        boxShadow: expanded ? HOVER_SHADOW : REST_SHADOW,
        transform: pressed ? `scale(${PRESSABLE_SCALE})` : 'scale(1)',
        transition: CONTAINER_TRANSITION,
      }}
    >
      <Plus
        className="shrink-0"
        width={ICON_SIZE}
        height={ICON_SIZE}
        strokeWidth={2.4}
        aria-hidden="true"
      />
      <span
        ref={labelRef}
        className="overflow-hidden whitespace-nowrap"
        style={{
          maxWidth: expanded ? labelWidth : 0,
          opacity: expanded ? 1 : 0,
          marginLeft: expanded ? GAP_PX : 0,
          transition: LABEL_TRANSITION,
        }}
      >
        {label}
      </span>
    </button>
  );
}
